// Step 25 — pure helpers for the AI Deal Command Center.
//
// The Command Center is a COMPOSITION layer over the intelligence
// Steps 1-24 already produce. It does NOT introduce a parallel
// action / contract / obligation / risk / execution / activity system.
//
// This module composes helpers from:
//   - Step 19 (intelligence/helpers) — KIND, SEVERITY, extractors
//   - Step 22 (buyers/helpers)       — buyer flags
//   - Step 23 (contracts/helpers)    — contract normalization, findings
//   - Step 24 (execution/helpers)    — action/contract/obligation rows,
//                                      blocking, connections, timeline
//
// Guardrails:
//   - NO fabricated deal-health score, closing probability, readiness
//     percentage, confidence, or synthetic overall traffic-light.
//   - Every derived row preserves source_type, source_id, as_of when
//     the backend supplied them. Missing provenance stays null.
//   - fact_or_inference labels ride through VERBATIM.
//   - Cross-domain connections come ONLY from explicit IDs the
//     backend returns (via Step 24's executionConnections). No text
//     matching. No name matching.
//   - Backend has NO obligation model (documented in AIObligations
//     since Step 18); the Command Center surfaces preparedness
//     observations (from Step 12 deal_situation) as "beredskapsgap"
//     rather than inventing an obligation model.

import { KIND, SEVERITY } from "../intelligence/helpers";
import {
  executionActionRows,
  executionActionCounts,
  executionAttentionFlags,
  executionBlockingItems,
  executionBuyerRows,
  executionConnections,
  executionContractCounts,
  executionContractRows,
  executionObligationRows,
  executionRiskRows,
  executionTimelineItems,
  READINESS_LANGUAGE,
} from "../execution/helpers";


// ── Command-scoped readiness language (shared with Step 24) ────────
//
// We deliberately reuse the same neutral wording set from Step 24 so
// nothing drifts across the two workspaces.
export const COMMAND_LANGUAGE = READINESS_LANGUAGE;


// ── Section titles the Command Center uses ─────────────────────────
export const COMMAND_SECTION = {
  ATTENTION: "Kräver uppmärksamhet",
  ACTIONS: "Åtgärder",
  OBLIGATIONS: "Åtaganden & beredskapsgap",
  RISKS: "Risker",
  CONTRACTS: "Avtal",
  BUYERS: "Köpare",
  EXECUTION: "Genomförande",
  ACTIVITY: "Senaste aktivitet",
  CONNECTIONS: "Kopplingar mellan domäner",
  FINDINGS: "Fynd",
};


// ── Row projections (thin re-exports from Step 24 helpers) ─────────
//
// The Command Center never re-derives the shapes Step 24 already
// established. If the Step 24 helper is enough, we simply forward.

export function commandActionRows(actions, opts) {
  return executionActionRows(actions, opts);
}

export function commandContractRows(contracts) {
  return executionContractRows(contracts);
}

export function commandObligationRows(situation) {
  return executionObligationRows(situation);
}

export function commandRiskRows(situation) {
  return executionRiskRows(situation?.deal);
}

export function commandBuyerRows(situation) {
  return executionBuyerRows(situation?.buyer_portfolio);
}


// ── Command counts ─────────────────────────────────────────────────
//
// Deterministic counters. Failed sources are represented by `null`
// (not 0) so the UI can render "—" honestly. Callers pass `null` for
// a failed source, [] for an empty-but-loaded source.

export function commandCounts({
  actions,
  contracts,
  situation,
  activityCount = null,
  now,
} = {}) {
  const aCounts = actions == null
    ? null
    : executionActionCounts(actions, { now });
  const cCounts = contracts == null
    ? null
    : executionContractCounts(contracts);
  const risks = situation == null
    ? null
    : commandRiskRows(situation);
  const buyers = situation == null
    ? null
    : commandBuyerRows(situation);
  const preparedness = situation == null
    ? null
    : commandObligationRows(situation);
  return {
    actions_open: aCounts ? aCounts.open : null,
    actions_blocked: aCounts ? aCounts.blocked : null,
    actions_overdue: aCounts ? aCounts.overdue : null,
    actions_pending_review: aCounts ? aCounts.pending_review : null,
    actions_completed: aCounts ? aCounts.completed : null,
    contracts_total: cCounts ? cCounts.total : null,
    contracts_pending_review: cCounts ? cCounts.pending_review : null,
    contracts_with_uncertainties: cCounts ? cCounts.with_uncertainties : null,
    contracts_requires_attention: cCounts ? cCounts.requires_attention : null,
    preparedness_gaps: preparedness == null ? null : preparedness.length,
    risks_total: risks == null ? null : risks.length,
    risks_high: risks == null ? null : risks.filter((r) => r.severity === SEVERITY.HIGH).length,
    buyers_total: buyers == null ? null : buyers.length,
    buyers_silent: buyers == null ? null : buyers.filter((b) => b.is_silent).length,
    activity_recent: activityCount,
  };
}


// ── Attention chips (workspace-level) ──────────────────────────────
//
// Reuses Step 24's `executionAttentionFlags`. Command Center adds one
// activity flag when the backend has produced ANY new
// snapshot/notification the caller could act on.

export function commandAttentionFlags({
  actions,
  contracts,
  situation,
  snapshots,
  notifications,
  now = Date.now(),
} = {}) {
  const flags = executionAttentionFlags({ actions, contracts, situation, now });
  const unread = (notifications || []).filter((n) => n && n.is_read === false).length;
  if (unread > 0) {
    flags.push({
      kind: "notifications_unread",
      severity: SEVERITY.MEDIUM,
      label: `${unread} olästa notiser`,
      title: COMMAND_LANGUAGE.FOLLOW_UP,
    });
  }
  const recentSnapshots = Array.isArray(snapshots) ? snapshots.length : 0;
  if (recentSnapshots > 0) {
    flags.push({
      kind: "activity_snapshots",
      severity: SEVERITY.LOW,
      label: `${recentSnapshots} snapshot${recentSnapshots === 1 ? "" : "s"}`,
      title: "Deal Situation-snapshots registrerade",
    });
  }
  return flags;
}


// ── Executive attention items (aggregated across domains) ──────────
//
// Reuses Step 24 `executionBlockingItems` and augments with unread
// notifications addressed to the current user. Every row identifies
// its source.

export function commandAttentionItems({
  actions,
  contracts,
  situation,
  notifications,
  now = Date.now(),
} = {}) {
  const items = executionBlockingItems({ actions, contracts, situation, now });

  for (const n of notifications || []) {
    if (!n || n.is_read !== false) continue;
    items.push({
      key: `notification.${n.id}`,
      category: "activity",
      severity: SEVERITY.MEDIUM,
      title: n.title || `Notis #${n.id}`,
      reason: COMMAND_LANGUAGE.FOLLOW_UP,
      source: {
        source_type: "notification",
        source_id: String(n.id),
        as_of: n.created_at || null,
      },
      action_id: n.deal_action ? n.deal_action.id : null,
    });
  }

  return items;
}


// ── Activity rows (deterministic recent activity) ──────────────────
//
// The two authoritative activity streams that already exist:
//   - DealSituationSnapshot (Step 12, workspace-scoped audit)
//   - Notification (Step 14, recipient-scoped)
//
// AIActivityPage already composes these two; the Command Center
// forwards a compact projection with source_type + as_of preserved.

export function commandActivityRows({ snapshots, notifications } = {}) {
  const out = [];
  for (const s of snapshots || []) {
    if (!s || s.id == null) continue;
    // Prefer `captured_at` (from Step 12 snapshot_summary); fall back
    // to `created_at` if only that field is available on the row shape.
    const as_of = s.captured_at || s.created_at || null;
    if (!as_of) continue;
    out.push({
      key: `activity.snapshot.${s.id}`,
      kind: "snapshot",
      title: `Deal Situation-snapshot v${s.version ?? "?"}`,
      as_of,
      source: {
        source_type: "deal_situation_snapshot",
        source_id: String(s.id),
        as_of,
      },
      counters: {
        buyer_count: s.buyer_count ?? null,
        open_items_count: s.open_items_count ?? null,
        silence_signal_count: s.silence_signal_count ?? null,
        preparedness_gap_count: s.preparedness_gap_count ?? null,
      },
    });
  }
  for (const n of notifications || []) {
    if (!n || n.id == null || !n.created_at) continue;
    out.push({
      key: `activity.notif.${n.id}`,
      kind: "notification",
      title: n.title || `Notis #${n.id}`,
      as_of: n.created_at,
      source: {
        source_type: "notification",
        source_id: String(n.id),
        as_of: n.created_at,
      },
      is_read: n.is_read === true,
      notification_type: n.notification_type || null,
      action_id: n.deal_action ? n.deal_action.id : null,
    });
  }
  return out.sort((a, b) => Date.parse(b.as_of) - Date.parse(a.as_of));
}


// ── Cross-domain connections (reuse Step 24) ───────────────────────

export function commandConnections({ actions, contracts, situation } = {}) {
  return executionConnections({ actions, contracts, situation });
}


// ── Command findings — one shared list, using existing KIND vocab ──
//
// The Command Center never invents finding kinds; it uses the same
// KIND enum Step 19-24 already ship. Findings come from:
//   - Step 24 blocking items (actions blocked/overdue, contracts
//     pending review, contract uncertainties, preparedness gaps)
//   - unread notifications assigned to the current user
//
// AIFindingCard renders each entry with the source panel via
// AIEvidencePanel — same visual language as everywhere else.

function _mapBlockingToFinding(item) {
  const base = {
    severity: item.severity,
    title: item.title,
    summary: item.reason,
    source: item.source || null,
    evidence: [],
    ask_prompt: null,
    user_action_required: true,
    fact_or_inference: item.fact_or_inference || null,
    key: `command.${item.key}`,
  };
  if (item.category === "action") {
    return {
      ...base,
      kind: KIND.ACTION_PENDING,
      category: "Åtgärd",
      action_id: item.action_id ?? null,
      acquirer_id: item.acquirer_id ?? null,
    };
  }
  if (item.category === "contract") {
    return {
      ...base,
      kind: item.key.startsWith("contract.review.")
        ? KIND.CONTRACT_REVIEW
        : KIND.CONTRACT_UNCERTAINTY,
      category: item.key.startsWith("contract.review.") ? "Kontrakt" : "Kontraktsfynd",
      contract_id: item.contract_id ?? null,
      acquirer_id: item.acquirer_id ?? null,
    };
  }
  if (item.category === "preparedness") {
    return {
      ...base,
      kind: KIND.PREPAREDNESS,
      category: "Beredskap",
    };
  }
  if (item.category === "activity") {
    return {
      ...base,
      kind: KIND.ACTION_PENDING,
      category: "Aktivitet",
      action_id: item.action_id ?? null,
    };
  }
  return { ...base, kind: KIND.SUGGESTED_ACTION, category: "Observation" };
}

export function commandFindings({
  actions,
  contracts,
  situation,
  notifications,
  now = Date.now(),
} = {}) {
  const items = commandAttentionItems({
    actions, contracts, situation, notifications, now,
  });
  return items.map(_mapBlockingToFinding);
}


// ── Timeline (reuse Step 24) ───────────────────────────────────────

export function commandTimelineItems(opts) {
  return executionTimelineItems(opts);
}


// ── Ask Qada prompts ───────────────────────────────────────────────

export function buildCommandAskPrompt({
  workspaceName,
  question,
} = {}) {
  const workspace = workspaceName ? ` för affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Analysera den aktuella affären${workspace} utifrån de ` +
    `deterministiska observationerna i Command Center. Identifiera ` +
    `vad som kräver mänsklig uppföljning, vilka öppna åtgärder, ` +
    `beredskapsgap (preparedness_observations), risker, avtalsfrågor ` +
    `och köparobservationer som är relevanta, och vilka samband som ` +
    `faktiskt kan styrkas av systemets data. Använd get_deal_situation, ` +
    `get_deal_actions, list_contract_drafts, get_deal_risks, ` +
    `get_deal_activity och get_buyer_intelligence där det behövs. ` +
    `Separera FAKTA, DETERMINISTISK SYSTEMOBSERVATION, DIN INFERENS, ` +
    `FÖRESLAGEN ÅTGÄRD och MÄNSKLIGT BESLUT. Ange källor och IDs där ` +
    `de finns.${suffix}`
  );
}

export function buildCommandInvestigationPrompt({
  workspaceName,
  topic,
} = {}) {
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const focus = topic ? `Fokusera på: ${topic}. ` : "";
  return (
    `${focus}Utred nuvarande deal-läge${workspace} enligt AI-DEAL ` +
    `COMMAND CENTER / ORKESTRERINGSUTREDNINGAR-flödet: etablera deal-` +
    `identitet, hämta deal_situation, deal_actions (open + blocked + ` +
    `overdue), preparedness/obligations (via situation), risker, ` +
    `contract_drafts, köparintelligens och deal_activity. Korrelera ` +
    `endast via auktoritativa IDs. Uppfinn ALDRIG att affären är ` +
    `klar, redo att undertecknas, eller att köparen har accepterat ` +
    `utan att backend explicit säger det. Föreslå eventuella nya ` +
    `steg endast via propose_deal_action (status blir 'proposed', ` +
    `mänsklig granskning krävs).`
  );
}
